import React, { useState } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const DashboardLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const navigation = [
    {
      name: "Dashboard",
      href: "/dashboard",
      current: location.pathname === "/dashboard",
    },
    {
      name: "Transactions",
      href: "/transactions",
      current: location.pathname === "/transactions",
    },
    {
      name: "Accounts",
      href: "/accounts",
      current: location.pathname === "/accounts",
    },
    {
      name: "Investments",
      href: "/investments/platforms",
      current: location.pathname.startsWith("/investments"),
    },
    {
      name: "Companies",
      href: "/companies",
      current: location.pathname.startsWith("/companies"),
    },
       {
      name: "Clients",
      href: "/clients",
      current: location.pathname.startsWith("/clients"),
    },
  ];

  return (
    <div className="h-screen flex bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
      {showSignOutDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-xs">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Sign out?
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to sign out?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 text-sm rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => setShowSignOutDialog(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm rounded bg-red-500 text-white hover:bg-red-600"
                onClick={() => {
                  setShowSignOutDialog(false);
                  logout();
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        </div>
      )}

      {/* Sidebar */}
      <aside
        className={`h-screen flex flex-col w-64 bg-white border-r border-gray-100 shadow-lg transform ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out fixed top-0 left-0 z-50 md:translate-x-0 md:static md:inset-0`}
        aria-label="Sidebar"
      >
        <div className="flex items-center h-14 px-6 bg-white border-b border-gray-100 shadow-sm">
          <svg
            className="w-7 h-7 text-red-500 mr-2"
            fill="none"
            viewBox="0 0 32 32"
            stroke="currentColor"
          >
            <rect x="4" y="4" width="24" height="24" rx="6" fill="#ef4444" />
            <text
              x="16"
              y="22"
              textAnchor="middle"
              fontSize="12"
              fill="white"
              fontWeight="bold"
            >
              F
            </text>
          </svg>
          <span className="text-xl font-bold tracking-tight text-red-500">
            FinNote
          </span>
        </div>
        <nav className="flex-1 px-3 py-2 space-y-1">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                item.current
                  ? "bg-red-100 text-red-700"
                  : "text-gray-700 hover:bg-gray-50 hover:text-red-600"
              }`}
              onClick={() => setIsSidebarOpen(false)}
            >
              <span className="flex-1">{item.name}</span>
            </Link>
          ))}
        </nav>
        <div className="px-6 py-5 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow">
              <span className="text-white text-sm font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-medium text-gray-800 truncate">
                {user?.name}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => setShowSignOutDialog(true)}
            className="mt-4 w-full flex items-center justify-center px-3 py-2 text-sm text-red-600 bg-white border border-red-100 rounded-lg shadow hover:bg-red-50 transition-all"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen w-full ml:32">
        {/* Top bar for mobile */}
        <div className="sticky top-0 z-30 flex-shrink-0 flex h-16 bg-white shadow md:hidden">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500"
            onClick={() => setIsSidebarOpen(true)}
          >
            <svg
              className="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <div className="flex items-center px-4">
            <span className="text-xl font-bold text-red-500">FinNote</span>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
